import React, { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Button,
  FormControlLabel,
  Checkbox,
  Grid,
  TextField,
  Typography,
  Box,
  FormGroup,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import { contactsTypesActions } from "../../../../../store/contactTypes/contactTypesActions";

const useStyles = makeStyles(theme => ({
  container: {
    padding: "20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  title: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
  },
  reminderList: {
    marginTop: "20px",
  },
  reminderCard: {
    marginBottom: "15px",
    borderLeft: "4px solid #3699FF",
    "&:hover": {
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    },
  },
  reminderHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "10px",
  },
  reminderDate: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
  },
  reminderActions: {
    display: "flex",
    gap: "8px",
  },
  reminderDetails: {
    marginTop: "10px",
  },
  chip: {
    marginRight: "8px",
    marginBottom: "8px",
  },
  noReminders: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#666",
  },
  section: {
    margin: "20px 0",
    padding: "20px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
  dateContainer: {
    marginBottom: "15px",
  },
  checkboxGroup: {
    marginTop: "10px",
  },
  checkboxLabel: {
    fontSize: "14px",
  },
  selectedReminders: {
    marginTop: "10px",
    padding: "10px",
    backgroundColor: "#e8f5e8",
    borderRadius: "4px",
    border: "1px solid #4caf50",
  },
  dialogTitle: {
    backgroundColor: "#3699FF",
    color: "white",
    padding: "16px 24px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
  },
  deleteProgress: {
    width: "20px !important",
    height: "20px !important",
  },
  cancelButton: {
    textTransform: "none !important",
  },
  disabledCheckbox: {
    color: "#999 !important",
  },
  warningText: {
    color: "#f57c00",
    fontSize: "14px",
    marginTop: "8px",
    fontStyle: "italic",
  },
}));

const ProspectFollowUpTab = ({ prospectData }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  // States
  const [reminders, setReminders] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [followUpDate, setFollowUpDate] = useState("");
  const [reminderOptions, setReminderOptions] = useState({
    "1_day": false,
    "2_days": false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Load reminders from API on component mount
  useEffect(() => {
    loadReminders();
  }, [prospectData?.id]);

  const loadReminders = async () => {
    if (!prospectData?.id) return;

    setIsLoading(true);
    try {
      const payLoad = {
        prospectId: prospectData?.id,
        dateFrom: "",
        dateTo: "",
        pagination: {
          pageSize: 1000,
          pageNo: 0,
          orderByKey: "id",
          orderByVal: 0,
        },
      };
      const response = await dispatch(
        contactsTypesActions.onGetProspectFollowUp(payLoad)
      );

      if (response?.data?.success) {
        setReminders(response.data.data.data);
      }
    } catch (error) {
      console.error("Error loading reminders:", error);
      toast.error("Failed to load reminders");
    } finally {
      setIsLoading(false);
    }
  };

  // Get today's date in YYYY-MM-DD format for min date
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Check if selected date is today
  const isTodaySelected = () => {
    if (!followUpDate) return false;

    const selectedDate = new Date(followUpDate);
    const today = new Date();

    // Compare year, month, and day
    return (
      selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate()
    );
  };

  // Handle checkbox change
  const handleCheckboxChange = event => {
    const { name, checked } = event.target;

    // If today is selected, don't allow setting reminders
    if (isTodaySelected()) {
      toast.warning("Cannot set reminders for today's date");
      return;
    }

    setReminderOptions(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Handle date change
  const handleDateChange = e => {
    const newDate = e.target.value;
    setFollowUpDate(newDate);

    // If the new date is today, reset reminder options
    const selectedDate = new Date(newDate);
    const today = new Date();

    const isToday =
      selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate();

    if (isToday) {
      setReminderOptions({
        "1_day": false,
        "2_days": false,
      });
      toast.info("Reminder options disabled for today's date");
    }
  };

  // Open dialog for adding new reminder
  const handleAddReminder = () => {
    setEditingReminder(null);
    setFollowUpDate("");
    setReminderOptions({
      "1_day": false,
      "2_days": false,
    });
    setIsDialogOpen(true);
  };

  // Open dialog for editing reminder
  const handleEditReminder = reminder => {
    setEditingReminder(reminder);

    // Convert the API date format "11-Nov-2025" to YYYY-MM-DD for the date input
    try {
      const apiDate = reminder.interactionDate; // "11-Nov-2025"
      const parsedDate = new Date(apiDate);
      if (!isNaN(parsedDate.getTime())) {
        const formattedDate = parsedDate.toISOString().split("T")[0];
        setFollowUpDate(formattedDate);
      } else {
        setFollowUpDate("");
      }
    } catch (error) {
      console.error("Error parsing date:", error);
      setFollowUpDate("");
    }

    setReminderOptions({
      "1_day": reminder.firstReminderDate,
      "2_days": reminder.secondReminderDate,
    });
    setIsDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingReminder(null);
  };

  // Delete reminder
  const handleDeleteReminder = async reminderId => {
    if (window.confirm("Are you sure you want to delete this reminder?")) {
      setDeletingId(reminderId);
      try {
        const response = await dispatch(
          contactsTypesActions.onDeleteProspectFollowUp(reminderId)
        );

        if (response?.data?.success) {
          const updatedReminders = reminders.filter(
            reminder => reminder.id !== reminderId
          );
          setReminders(updatedReminders);
          toast.success("Reminder deleted successfully!");
        } else {
          toast.error("Failed to delete reminder");
        }
      } catch (error) {
        console.error("Error deleting reminder:", error);
        toast.error("Failed to delete reminder");
      } finally {
        setDeletingId(null);
      }
    }
  };

  // Save follow-up settings
  const handleSaveFollowUp = useCallback(async () => {
    if (!followUpDate) {
      toast.error("Please select a date for follow-up");
      return;
    }

    const selectedReminders = Object.keys(reminderOptions).filter(
      key => reminderOptions[key]
    );

    // Only check for reminder selection if date is not today
    if (!isTodaySelected() && selectedReminders.length === 0) {
      toast.error("Please select at least one reminder option");
      return;
    }

    setIsSaving(true);

    try {
      // Create follow-up date - use the date as is without timezone conversion
      // The date picker gives us YYYY-MM-DD format, we need to ensure it stays as that date
      const followUpDateTime = new Date(followUpDate);

      // Validate the date
      if (isNaN(followUpDateTime.getTime())) {
        toast.error("Invalid date selected");
        setIsSaving(false);
        return;
      }

      // Check if date is in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (followUpDateTime < today) {
        toast.error("Please select a future date for follow-up");
        setIsSaving(false);
        return;
      }

      // Format the date to YYYY-MM-DD without timezone issues
      // This ensures the date stays as selected regardless of timezone
      const year = followUpDateTime.getFullYear();
      const month = String(followUpDateTime.getMonth() + 1).padStart(2, "0");
      const day = String(followUpDateTime.getDate()).padStart(2, "0");
      const isoDateString = `${year}-${month}-${day}`;

      // Prepare payload according to API requirements
      const payload = {
        id: editingReminder ? editingReminder.id : 0, // 0 for new reminders
        type: 1, // Always 1
        followupDate: isoDateString, // Use the properly formatted ISO string
        comments: "", // Empty string as required
        source: "prospect", // Always "prospect"
        prospectId: prospectData?.id,
        isRemindMe: true, // Always true
        firstReminderDate: reminderOptions["1_day"], // true/false based on checkbox
        secondReminderDate: reminderOptions["2_days"], // true/false based on checkbox
      };

      console.log("Sending payload:", payload);

      // Make API call
      const response = await dispatch(
        contactsTypesActions.onAddProspectFollowUp(payload)
      );

      if (response?.data?.success) {
        // Refresh the reminders list
        await loadReminders();

        toast.success(
          editingReminder
            ? "Reminder updated successfully!"
            : "Follow-up reminder set successfully!"
        );

        // Show success message with details
        const reminderText = selectedReminders
          .map(option => {
            if (option === "1_day") return "1 day before";
            if (option === "2_days") return "2 days before";
            return option;
          })
          .join(" and ");

        // toast.info(`Reminders set for ${reminderText}`);
        handleCloseDialog();
      } else {
        toast.error("Failed to save follow-up settings");
      }
    } catch (error) {
      console.error("Follow-up save error:", error);
      toast.error("Failed to save follow-up settings");
    } finally {
      setIsSaving(false);
    }
  }, [followUpDate, reminderOptions, prospectData, editingReminder, dispatch]);

  // Format date for display
  const formatDisplayDate = dateString => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  // Check if reminder is upcoming
  const isUpcoming = dateString => {
    try {
      const reminderDate = new Date(dateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return reminderDate >= today;
    } catch (error) {
      console.error("Error checking date:", error);
      return false;
    }
  };

  // Get selected reminders text
  const getSelectedRemindersText = () => {
    const selected = [];
    if (reminderOptions["1_day"]) selected.push("1 day before");
    if (reminderOptions["2_days"]) selected.push("2 days before");
    return selected.join(" and ");
  };

  // Check if form can be saved
  const canSaveFollowUp =
    followUpDate &&
    (isTodaySelected() ||
      Object.values(reminderOptions).some(option => option));

  return (
    <div className={classes.container}>
      {/* Header with Add Button */}
      <div className={classes.header}>
        <Typography className={classes.title}>Follow-up Reminders</Typography>
        <Button
          variant='contained'
          color='primary'
          startIcon={<AddIcon />}
          onClick={handleAddReminder}>
          Add Reminder
        </Button>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
        </div>
      ) : (
        /* Reminders List */
        <div className={classes.reminderList}>
          {reminders.length === 0 ? (
            <div className={classes.noReminders}>
              <ScheduleIcon
                style={{ fontSize: 48, color: "#ccc", marginBottom: 16 }}
              />
              <Typography variant='h6' color='textSecondary'>
                No reminders set
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                Click "Add Reminder" to create your first follow-up reminder
              </Typography>
            </div>
          ) : (
            reminders.map(reminder => (
              <Card key={reminder.id} className={classes.reminderCard}>
                <CardContent>
                  <div className={classes.reminderHeader}>
                    <Typography className={classes.reminderDate}>
                      {formatDisplayDate(reminder.interactionDate)}
                    </Typography>
                    <div className={classes.reminderActions}>
                      <IconButton
                        size='small'
                        onClick={() => handleEditReminder(reminder)}>
                        <EditIcon />
                      </IconButton>
                      {/* <IconButton
                        size='small'
                        onClick={() => handleDeleteReminder(reminder.id)}
                        disabled={deletingId === reminder.id}>
                        {deletingId === reminder.id ? (
                          <CircularProgress
                            className={classes.deleteProgress}
                          />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton> */}
                    </div>
                  </div>

                  <div className={classes.reminderDetails}>
                    <Box mb={1}>
                      {reminder.firstReminderDate && (
                        <Chip
                          label='1 day before'
                          color='primary'
                          variant='outlined'
                          size='small'
                          className={classes.chip}
                        />
                      )}
                      {reminder.secondReminderDate && (
                        <Chip
                          label='2 days before'
                          color='primary'
                          variant='outlined'
                          size='small'
                          className={classes.chip}
                        />
                      )}
                    </Box>
                    <Typography variant='body2' color='textSecondary'>
                      Status:{" "}
                      {isUpcoming(reminder.interactionDate)
                        ? "Upcoming"
                        : "Completed"}
                    </Typography>
                    {!isUpcoming(reminder.interactionDate) && (
                      <Chip
                        label='Completed'
                        color='default'
                        size='small'
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Add/Edit Reminder Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth>
        <DialogTitle className={classes.dialogTitle}>
          {editingReminder ? "Edit Reminder" : "Add New Reminder"}
        </DialogTitle>

        <DialogContent>
          <div className={classes.section}>
            <Typography className={classes.title}>
              Follow-up Schedule
            </Typography>

            <Grid container spacing={2} className={classes.dateContainer}>
              <Grid item xs={12}>
                <TextField
                  label='Follow-up Date'
                  type='date'
                  value={followUpDate}
                  onChange={handleDateChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: getTodayDate(),
                  }}
                  fullWidth
                  variant='outlined'
                  error={!followUpDate}
                  helperText={!followUpDate ? "Follow-up date is required" : ""}
                />
              </Grid>
            </Grid>

            <Typography className={classes.note}>
              Select when you want to follow up with this prospect.
            </Typography>
          </div>

          <div className={classes.section}>
            <Typography className={classes.title}>Reminder Settings</Typography>

            <FormGroup className={classes.checkboxGroup}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reminderOptions["1_day"]}
                    onChange={handleCheckboxChange}
                    name='1_day'
                    color='primary'
                    disabled={isTodaySelected()}
                    className={
                      isTodaySelected() ? classes.disabledCheckbox : ""
                    }
                  />
                }
                label='Remind me 1 day before'
                className={`${classes.checkboxLabel} ${
                  isTodaySelected() ? classes.disabledCheckbox : ""
                }`}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reminderOptions["2_days"]}
                    onChange={handleCheckboxChange}
                    name='2_days'
                    color='primary'
                    disabled={isTodaySelected()}
                    className={
                      isTodaySelected() ? classes.disabledCheckbox : ""
                    }
                  />
                }
                label='Remind me 2 days before'
                className={`${classes.checkboxLabel} ${
                  isTodaySelected() ? classes.disabledCheckbox : ""
                }`}
              />
            </FormGroup>

            {isTodaySelected() && (
              <Typography className={classes.warningText}>
                Reminder options are not available for today's date
              </Typography>
            )}

            {!isTodaySelected() &&
              Object.values(reminderOptions).some(option => option) && (
                <Box className={classes.selectedReminders}>
                  <Typography variant='body2'>
                    <strong>Selected reminders:</strong>{" "}
                    {getSelectedRemindersText()}
                  </Typography>
                </Box>
              )}

            {!isTodaySelected() &&
              !Object.values(reminderOptions).some(option => option) && (
                <Typography variant='body2' color='error'>
                  Please select at least one reminder option
                </Typography>
              )}

            <Typography className={classes.note}>
              Choose when you want to be reminded about this follow-up. You can
              select multiple options.
            </Typography>
          </div>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            className={classes.cancelButton}
            disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveFollowUp}
            color='primary'
            variant='contained'
            disabled={!canSaveFollowUp || isSaving}>
            {isSaving ? (
              <CircularProgress size={24} />
            ) : editingReminder ? (
              "Update Reminder"
            ) : (
              "Save Reminder"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProspectFollowUpTab;
